import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Lock, AlertTriangle, Loader2, BarChart3, Award, MapPin, CalendarDays, Flag, Scale, Users, Megaphone, Sparkles, Zap, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useExhibitionEventState } from "@/hooks/useExhibitionEventState";
import ExhibitionEventBuilder from "@/components/exhibition/ExhibitionEventBuilder";
import ExhibitionQuickActions from "@/components/exhibition/ExhibitionQuickActions";
import AdminEventScheduler from "@/components/exhibition/AdminEventScheduler";
import ExhibitionCityLeaderboard from "@/components/exhibition/ExhibitionCityLeaderboard";
import ExhibitionAnalyticsDashboard from "@/components/exhibition/ExhibitionAnalyticsDashboard";
import AdminBadgeAward from "@/components/exhibition/AdminBadgeAward";
import AdminReportsPanel from "@/components/admin/AdminReportsPanel";
import AdminAppealsPanel from "@/components/admin/AdminAppealsPanel";
import AdminUserManagementPanel from "@/components/admin/AdminUserManagementPanel";
import AdminAnnouncementPanel from "@/components/admin/AdminAnnouncementPanel";
import BackgroundBalloons from "@/components/pop-balloon/BackgroundBalloons";
import { cn } from "@/lib/utils";

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const { isAdminOrModerator, isAdmin, isModerator, loading } = useAdminRole();
  const { eventData, eventState, isAdminOverride, isPaused, isPublished, displayTitle, displayMessage, debugInfo, refresh: refetchEventState } = useExhibitionEventState();
  const [activeTab, setActiveTab] = useState<"users" | "events" | "analytics" | "badges" | "cities" | "reports" | "appeals" | "announce">("events");
  const [showDebug, setShowDebug] = useState(false);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen sky-gradient flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Access denied
  if (!isAdminOrModerator) {
    return (
      <div className="min-h-screen sky-gradient relative overflow-hidden">
        <BackgroundBalloons />
        
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6">
          <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6">
            <Lock className="w-10 h-10 text-red-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-foreground mb-2 text-center">
            Access Denied
          </h1>
          <p className="text-muted-foreground text-center mb-6 max-w-sm">
            You need administrator or moderator privileges to access this area.
          </p>

          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen sky-gradient relative overflow-hidden">
      <BackgroundBalloons />
      
      <div className="relative z-10 flex flex-col min-h-screen pb-24">
        {/* Header */}
        <div className="px-4 pt-4 pb-2 sticky top-0 z-20 backdrop-blur-xl bg-background/80 border-b border-white/20">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2">
              <ArrowLeft className="w-6 h-6 text-foreground" />
            </button>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <span className="font-bold text-foreground">Admin Panel</span>
            </div>
            <div className="w-10" /> {/* Spacer */}
          </div>
        </div>

        {/* Role indicator */}
        <div className="mx-4 mt-4">
          <div className="flex items-center gap-2 p-3 rounded-xl bg-primary/10 border border-primary/20">
            <Shield className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm font-medium text-foreground">
                {isAdmin ? "Administrator" : "Moderator"}
              </p>
              <p className="text-xs text-muted-foreground">
                {isAdmin 
                  ? "Full access to all admin features" 
                  : "Limited access to moderation features"}
              </p>
            </div>
          </div>
        </div>

        {/* Admin warning */}
        <div className="mx-4 mt-4">
          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Admin Actions
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Changes made here affect all users. Please proceed carefully.
              </p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mx-4 mt-4">
          <div className="flex rounded-lg bg-muted/30 p-1 overflow-x-auto">
            {[
              { key: "users", label: "Users", icon: Users },
              { key: "analytics", label: "Analytics", icon: BarChart3 },
              { key: "announce", label: "Announce", icon: Megaphone },
              { key: "reports", label: "Reports", icon: Flag },
              { key: "appeals", label: "Appeals", icon: Scale },
              { key: "events", label: "Events", icon: CalendarDays },
              { key: "badges", label: "Badges", icon: Award },
              { key: "cities", label: "Cities", icon: MapPin },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as typeof activeTab)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap",
                  activeTab === key
                    ? "bg-white dark:bg-gray-800 shadow text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content sections */}
        <div className="px-4 mt-6 space-y-6">
          {/* User Management */}
          {activeTab === "users" && (
            <section className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-4">
              <AdminUserManagementPanel />
            </section>
          )}

          {/* Analytics Dashboard */}
          {activeTab === "analytics" && (
            <section>
              <ExhibitionAnalyticsDashboard />
            </section>
          )}

          {/* Announcements Panel */}
          {activeTab === "announce" && (
            <section className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-4">
              <AdminAnnouncementPanel />
            </section>
          )}

          {/* Reports Panel */}
          {activeTab === "reports" && (
            <section className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-4">
              <AdminReportsPanel />
            </section>
          )}
          
          {/* Appeals Panel */}
          {activeTab === "appeals" && (
            <section className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-4">
              <AdminAppealsPanel />
            </section>
          )}
          {activeTab === "events" && (
            <div className="space-y-6">
              {/* Event Builder - Primary Section */}
              <section className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-6">
                <ExhibitionEventBuilder
                  eventId={eventData?.id ?? null}
                  eventData={eventData ? {
                    name: eventData.name,
                    starts_at: eventData.starts_at,
                    ends_at: eventData.ends_at,
                    display_title: displayTitle ?? null,
                    display_message: displayMessage ?? null,
                    is_published: isPublished,
                  } : null}
                  onEventUpdate={refetchEventState}
                />
              </section>

              {/* Quick Actions - Secondary Section */}
              <section className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-lg">Quick Actions</h3>
                    <p className="text-sm text-muted-foreground">Override schedule and adjust timing</p>
                  </div>
                  <div className="ml-auto">
                    <span className={cn(
                      "px-3 py-1 rounded-full text-xs font-semibold",
                      eventState === "live" && "bg-green-500/20 text-green-600",
                      eventState === "locked" && "bg-amber-500/20 text-amber-600",
                      eventState === "closed" && "bg-red-500/20 text-red-600",
                      eventState === "no-event" && "bg-gray-500/20 text-gray-600"
                    )}>
                      {eventState.toUpperCase()}
                    </span>
                  </div>
                </div>
                <ExhibitionQuickActions
                  eventId={eventData?.id ?? null}
                  eventState={eventState}
                  isAdminOverride={isAdminOverride}
                  isPaused={isPaused}
                  onStateChange={refetchEventState}
                />
              </section>

              {/* Debug Section - Collapsible */}
              {debugInfo && (
                <section className="rounded-xl bg-muted/30 border border-muted p-4">
                  <button
                    onClick={() => setShowDebug(!showDebug)}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <span className="text-sm font-medium text-muted-foreground">Debug Info</span>
                    {showDebug ? (
                      <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                  {showDebug && (
                    <pre className="mt-3 p-3 rounded-lg bg-muted text-xs overflow-auto max-h-48">
                      {JSON.stringify(debugInfo, null, 2)}
                    </pre>
                  )}
                </section>
              )}

              {/* All Events List */}
              <section className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60">
                <AdminEventScheduler />
              </section>
            </div>
          )}

          {/* Badge Awards */}
          {activeTab === "badges" && (
            <section>
              <AdminBadgeAward />
            </section>
          )}

          {/* City Leaderboard */}
          {activeTab === "cities" && (
            <section className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-4">
              <ExhibitionCityLeaderboard />
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
